import React from "react";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { Background } from "../../components/BuildingBlocks/Background";
import { FrontColors } from "../../models/FrontColors";
import { Flex } from "../../components/BuildingBlocks/Flex";
import { Padding } from "../../components/BuildingBlocks/Padding";
import { FrontSpacing } from "../../models/FrontSpacing";
import { FPPropertyReportForm } from "../../components/FrontProbate/PropertyReport/FPPropertyReportForm";
import { FPPropertyReportTitleSelect } from "../../components/FrontProbate/PropertyReport/FPPropertyReportTitleSelect";
import { FPPropertyReportResults } from "../../components/FrontProbate/PropertyReport/FPPropertyReportResults";
import { pipe } from "fp-ts/lib/function";
import { array, option } from "fp-ts";
import { PublicPropertyReportCreateForm, PublicPropertyReportTitleSearchForm, TPublicPropertyReportAddressSearchForm, TPublicPropertyReportCreateForm, TPublicPropertyReportTitleSearchForm } from "../../../../domain/codecs/form/PublicPropertyReportForm";
import { FPPropertyReportTransactionTypeQuestion } from "../../components/FrontProbate/PropertyReport/FPPropertyReportTransactionTypeQuestion";
import { FPPropertyReportAddressSelect } from "../../components/FrontProbate/PropertyReport/FPPropertyReportAddressSelect";
import { FPPropertyReportExecutorOrBeneficiary } from "../../components/FrontProbate/PropertyReport/FPPropertyReportExecutorOrBeneficiary";
import { FPPropertyReportExpiredAccount } from "../../components/FrontProbate/PropertyReport/FPPropertyReportExpiredAccount";
import { FPPropertyReportSignIn } from "../../components/FrontProbate/PropertyReport/FPPropertyReportSignIn";
import { FPPropertyReportNoFirmMatched } from "../../components/FrontProbate/PropertyReport/FPPropertyReportNoFirmMatched";
import { FPPropertyReportIntroducerSelect } from "../../components/FrontProbate/PropertyReport/FPPropertyReportIntroducerSelect";

export const FrontProbatePropertyReportContainer = (props: TContainerStateProps): JSX.Element => {
    const view = props.state.forms.public_property_report_page.view;

    const selectedAddressId = props.state.forms.public_property_report_page.selected_address_id;

    const addressForm = props.state.forms.public_property_report_page.form;

    const selectedTitleSearchForm: TPublicPropertyReportTitleSearchForm =
        pipe(
            addressForm.children.results,
            array.findFirst(({edited}) => edited.id === selectedAddressId),
            option.getOrElse(() => PublicPropertyReportTitleSearchForm.newDefault()),
        );

    const visibleTitleNumber = props.state.forms.public_property_report_page.visible_title_number;

    const visiblePropertyReportCreateForm: TPublicPropertyReportCreateForm =
        pipe(
            visibleTitleNumber !== null
            && selectedAddressId
            && selectedTitleSearchForm.children.response?._tag === "success"
                ? pipe(
                    selectedTitleSearchForm.children.response.results,
                    array.findFirst((result) => result.edited.title_number === visibleTitleNumber)
                )
                : option.none,
            option.fold(
                PublicPropertyReportCreateForm.newDefault,
                (f) => f,
            ),
        );

    const signedInContact = props.state.forms.public_property_report_page.signed_in_contact;

    const staffIntroducerOptions = props.state.forms.public_property_report_page.staff_introducer_options;

    const selectedStaffIntroducerId = props.state.forms.public_property_report_page.selected_staff_introducer_id;

    const selectedStaffIntroducerName =
        pipe(
            staffIntroducerOptions ?? [],
            array.findFirst(({id}) => id === selectedStaffIntroducerId),
            option.fold(() => "", ({name}) => name),
        );

    const isStaff = staffIntroducerOptions !== null;

    const onChangeStaffIntroducerFilter = (payload: string) =>
        props.dispatch({
            type: "PUBLIC_PROPERTY_REPORT_STAFF_INTRODUCER_FILTER_CHANGE",
            payload,
        });

    const onSelectStaffIntroducer = (payload: string) =>
        props.dispatch({
            type: "PUBLIC_PROPERTY_REPORT_STAFF_INTRODUCER_SELECTED",
            payload,
        });

    const onChangeStaffIntroducer = () =>
        props.dispatch({
            type: "PUBLIC_PROPERTY_REPORT_STAFF_INTRODUCER_CLEARED",
        });

    const signInEmail = props.state.forms.public_property_report_page.sign_in_email;

    const signInStatus = props.state.forms.public_property_report_page.sign_in_status;

    const onChangeSignInEmail = (payload: string) =>
        props.dispatch({
            type: "PUBLIC_PROPERTY_REPORT_SIGN_IN_EMAIL_CHANGE",
            payload,
        });

    const onSubmitSignIn = () =>
        props.dispatch({
            type: "PUBLIC_PROPERTY_REPORT_SIGN_IN_SUBMIT",
        });

    const onSubmitInitialForm = () =>
        props.dispatch({
            type: "PUBLIC_PROPERTY_REPORT_INITIAL_FORM_CONTINUE_PRESSED",
        });

    const onChangeAddressSearchForm =
        (payload: TPublicPropertyReportAddressSearchForm) =>
            props.dispatch({
                type: "PUBLIC_PROPERTY_REPORT_ADDRESS_SEARCH_CHANGE",
                payload,
            });

    const onSelectTransactionType =
        (payload: TPublicPropertyReportAddressSearchForm) =>
            props.dispatch({
                type: "PUBLIC_PROPERTY_REPORT_TRANSACTION_TYPE_CHANGE",
                payload,
            });

    const onSubmitAddressSearchForm = () =>
        props.dispatch({
            type: "PUBLIC_PROPERTY_REPORT_ADDRESS_SEARCH_SUBMIT",
        });

    const onChangeVisibleTitleNumber =
        (payload) =>
            props.dispatch({
                type: "PUBLIC_PROPERTY_REPORT_VISIBLE_TITLE_NUMBER_CHANGE",
                payload,
            });

    const onSelectAddressId =
        (payload: number) =>
            props.dispatch({
                type: "PUBLIC_PROPERTY_REPORT_SELECTED_ADDRESS_ID_CHANGE",
                payload,
            });

    return (
        <Background
            width="100%"
            minHeight="100vh"
            color={FrontColors.PROBATE_0}
        >
            <Flex justifyContent="center">
                <Padding spacing={FrontSpacing.LARGE_2}>

                    {/**
                     * The report form is gated on a session, not on the ?introducer_id= in the
                     * URL. That parameter is just a string anyone can edit, and while it named
                     * the introducer the form took the contact's identity from whatever was
                     * typed into it - which is how thousands of £7 Land Registry extracts came
                     * to be pulled against addresses like phony.com and test.com.
                     *
                     * A signed-in contact is one who could read mail at their firm's domain, so
                     * attribution comes from the session and the URL parameter no longer decides
                     * anything. An existing partner link still works; it just asks the person to
                     * sign in once first.
                     *
                     * The Sail Homes valuation funnel also submits with no introducer, but runs
                     * through ValuationFunnelContainer, so it is unaffected.
                     *
                     * Staff are checked first because they are never a contact - they pick the
                     * firm instead, every report, with nothing preselected.
                     */}
                    {isStaff ? (
                        <>
                            {/* INTRODUCER SELECT */}
                            {view === "staff_introducer_select" &&
                                <FPPropertyReportIntroducerSelect
                                    options={staffIntroducerOptions}
                                    filter={props.state.forms.public_property_report_page.staff_introducer_filter}
                                    onChangeFilter={onChangeStaffIntroducerFilter}
                                    onSelect={onSelectStaffIntroducer}
                                />
                            }

                            {/* FORM INITIAL */}
                            {view === "form_initial" &&
                                <FPPropertyReportForm
                                    selectedAddressId={selectedAddressId}
                                    form={addressForm}
                                    signedInContact={null}
                                    loggingOnBehalfOf={{
                                        introducerName: selectedStaffIntroducerName,
                                        onChangeIntroducer: onChangeStaffIntroducer,
                                    }}
                                    onChange={onChangeAddressSearchForm}
                                    onSubmit={onSubmitInitialForm}
                                />
                            }

                            {/* FORM TRANSACTION TYPE */}
                            {view === "form_transaction_type" &&
                                <FPPropertyReportTransactionTypeQuestion
                                    form={addressForm}
                                    onSelect={onSelectTransactionType}
                                />
                            }

                            {/* FORM EXECUTOR OR BENEFICIARY */}
                            {view === "form_executor_or_beneficiary" &&
                                <FPPropertyReportExecutorOrBeneficiary
                                    form={addressForm}
                                    onChange={onChangeAddressSearchForm}
                                    onSubmit={onSubmitAddressSearchForm}
                                />
                            }

                            {/* ADDRESS SELECT */}
                            {view === "address_select" &&
                                <FPPropertyReportAddressSelect
                                    form={addressForm}
                                    onSelect={onSelectAddressId}
                                />
                            }

                            {/* TITLE SELECT */}
                            {view === "title_select" &&
                                <FPPropertyReportTitleSelect
                                    form={selectedTitleSearchForm}
                                    onSelect={onChangeVisibleTitleNumber}
                                />
                            }

                            {/* RESULTS */}
                            {view === "results" &&
                                <FPPropertyReportResults
                                    form={visiblePropertyReportCreateForm}
                                />
                            }
                        </>
                    ) : view === "signed_in_without_introducer" ? (
                        <FPPropertyReportNoFirmMatched email={signInEmail || null} />
                    ) : signedInContact === null ? (
                        <FPPropertyReportSignIn
                            email={signInEmail}
                            status={signInStatus}
                            onChangeEmail={onChangeSignInEmail}
                            onSubmit={onSubmitSignIn}
                        />
                    ) : /**
                     * We are harcode deactivating KCT until they start delivering conversions. They need to speak to Jamie directly and this can be reactivated then.
                     */
                    signedInContact.introducer_id === "c385f4f2-3642-4c24-b519-0ad23600240a" ? (
                        <FPPropertyReportExpiredAccount />
                    ) : (
                        <>
                            {/* FORM INITIAL */}
                            {view === "form_initial" &&
                                <FPPropertyReportForm
                                    selectedAddressId={selectedAddressId}
                                    form={addressForm}
                                    signedInContact={signedInContact}
                                    loggingOnBehalfOf={null}
                                    onChange={onChangeAddressSearchForm}
                                    onSubmit={onSubmitInitialForm}
                                />
                            }

                            {/* FORM TRANSACTION TYPE */}
                            {view === "form_transaction_type" &&
                                <FPPropertyReportTransactionTypeQuestion
                                    form={addressForm}
                                    onSelect={onSelectTransactionType}
                                />
                            }

                            {/* FORM EXECUTOR OR BENEFICIARY */}
                            {view === "form_executor_or_beneficiary" &&
                                <FPPropertyReportExecutorOrBeneficiary
                                    form={addressForm}
                                    onChange={onChangeAddressSearchForm}
                                    onSubmit={onSubmitAddressSearchForm}
                                />
                            }

                            {/* ADDRESS SELECT */}
                            {view === "address_select" &&
                                <FPPropertyReportAddressSelect
                                    form={addressForm}
                                    onSelect={onSelectAddressId}
                                />
                            }

                            {/* TITLE SELECT */}
                            {view === "title_select" &&
                                <FPPropertyReportTitleSelect
                                    form={selectedTitleSearchForm}
                                    onSelect={onChangeVisibleTitleNumber}
                                />
                            }

                            {/* RESULTS */}
                            {view === "results" &&
                                <FPPropertyReportResults
                                    form={visiblePropertyReportCreateForm}
                                />
                            }
                        </>
                    )}
                </Padding>
            </Flex>
        </Background>
    );
};
